/* ============================================================================
   Dark-mode accent normalisation.

   Module accents (`EventModule.accentColor`) are picked by staff in the Event
   Module form and are chosen while looking at the LIGHT dashboard, where a deep
   hue reads as "premium". Sales Tax is stored as #1e3a5f — 1.05:1 against the
   dark card (--app-card #2c313a), i.e. an invisible glyph on an invisible tile.
   The value is stamped inline on the icon tile (`color` + `background`), so no
   `.dark-mode` rule can reach it; the correction has to happen in JS, before it
   is bound.

   `darkAccent()` keeps the author's hue and lifts the pair (S, L) until the
   colour clears MIN_CONTRAST on the dark card. Bright accents (amber, cyan,
   emerald) already clear it and come back untouched, so the dashboard keeps its
   per-module identity instead of collapsing to one safe blue.
   ============================================================================ */

// --app-card in dark mode. Icon tiles only ever sit on this tier.
const CARD_RGB = [44, 49, 58];

// WCAG contrast the glyph must reach on that tier. 5.0 sits just above the
// brightest existing accent (#f59e0b, 6.1:1) without over-lifting the rest.
const MIN_CONTRAST = 5.0;

// Chromatic accents get a saturation floor so a lifted colour reads as its hue
// rather than as washed-out grey. Below this the input is treated as a neutral
// and its greyness is preserved.
const CHROMA_THRESHOLD = 0.12;
const MIN_SATURATION = 0.58;

// Never go past this lightness — beyond it every hue converges on white.
const MAX_LIGHTNESS = 0.82;

const cache = new Map();

function parseHex(value) {
  if (typeof value !== "string") return null;
  let hex = value.trim().replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function toHex([r, g, b]) {
  const part = (n) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

function rgbToHsl([r, g, b]) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb([h, s, l]) {
  if (s === 0) {
    const v = l * 255;
    return [v, v, v];
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const channel = (t) => {
    let tc = t;
    if (tc < 0) tc += 1;
    if (tc > 1) tc -= 1;
    if (tc < 1 / 6) return p + (q - p) * 6 * tc;
    if (tc < 1 / 2) return q;
    if (tc < 2 / 3) return p + (q - p) * (2 / 3 - tc) * 6;
    return p;
  };
  return [channel(h + 1 / 3) * 255, channel(h) * 255, channel(h - 1 / 3) * 255];
}

function relativeLuminance([r, g, b]) {
  const lin = (c) => {
    const n = c / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastOnCard(rgb) {
  const a = relativeLuminance(rgb);
  const b = relativeLuminance(CARD_RGB);
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The dark-mode form of an accent: same hue, lifted until it is legible on
 * --app-card. Returns the input unchanged when it is already bright enough, and
 * passes through anything that is not a plain hex (tokens, named colours, null)
 * so callers can bind it blindly.
 *
 * @param {string} value e.g. "#1e3a5f"
 * @returns {string} e.g. "#6ba3dd"
 */
export function darkAccent(value) {
  if (cache.has(value)) return cache.get(value);

  const rgb = parseHex(value);
  if (!rgb) return value;

  let result = value;
  if (contrastOnCard(rgb) < MIN_CONTRAST) {
    const [h, s0, l0] = rgbToHsl(rgb);
    const s = s0 >= CHROMA_THRESHOLD ? Math.max(s0, MIN_SATURATION) : s0;

    // Walk lightness up in 1% steps — cheap, and it stops at the first value
    // that clears the bar instead of over-brightening a hue that gets there
    // early (yellow) or under-brightening one that does not (blue, violet).
    let candidate = hslToRgb([h, s, l0]);
    for (let l = l0; l <= MAX_LIGHTNESS; l += 0.01) {
      candidate = hslToRgb([h, s, l]);
      if (contrastOnCard(candidate) >= MIN_CONTRAST) break;
    }
    result = toHex(candidate);
  }

  cache.set(value, result);
  return result;
}
